import { JssStyle, Styles } from 'jss';
import {
  BreakpointCustomizable,
  buildResponsiveStylesWithProps,
  color as colorUtils,
  getClosestHTMLElement,
  getScreenReaderJssStyle,
  mergeDeep,
  buildResponsiveStyles,
  TValidationState,
  FULL_TEXT_STYLES,
} from '../../utils';

export const INPUT_LABEL_POSITION = ['default', 'left', 'above'] as const;
export type TInputLabelPosition = (typeof INPUT_LABEL_POSITION)[number];

export type TInputClearEvent = { clear: boolean };

export const isInsideForm = (host: HTMLElement): boolean => !!getClosestHTMLElement(host, 'form');

export const addInputEventListenerForSearch = (
  input: HTMLInputElement,
  inputChangeCallback: (hasValue: boolean) => void
): void => {
  input.addEventListener('input', (e: Event & { target: HTMLInputElement }) => {
    inputChangeCallback(!!e.target.value);
  });
  input.addEventListener('keydown', (e: KeyboardEvent & { target: HTMLInputElement }) => {
    if (e.key === 'Escape' && e.target.value) {
      e.preventDefault();
      e.target.value = '';
      // need to emit event so consumer's change listeners fire for resetting a search, etc.
      dispatchInputEvent(e.target);
    }
  });
};

export const dispatchInputEvent = (el: HTMLInputElement): void => {
  // { bubbles: true } is crucial for react onChange callback getting invoked
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

export const getTextInputColors = (
  state: TValidationState,
  disabled: boolean,
  readonly: boolean
): { baseLabelColor: string; baseInputColor: string; baseBorderColor: string } => {
  let baseLabelColor;
  let baseInputColor;
  let baseBorderColor;

  if (disabled) {
    baseLabelColor = colorUtils.grey02;
    baseInputColor = colorUtils.grey02;
    baseBorderColor = colorUtils.grey02;

    return { baseLabelColor, baseInputColor, baseBorderColor };
  }

  if (readonly) {
    baseLabelColor = colorUtils.grey06;
    baseInputColor = colorUtils.darkBlue02;
    baseBorderColor = colorUtils.grey02;

    return { baseLabelColor, baseInputColor, baseBorderColor };
  }

  switch (state) {
    case 'none':
      baseLabelColor = colorUtils.grey06;
      baseInputColor = colorUtils.darkBlue02;
      baseBorderColor = colorUtils.grey05;
      return { baseLabelColor, baseInputColor, baseBorderColor };
    case 'error':
      baseLabelColor = colorUtils.grey06;
      baseInputColor = colorUtils.darkBlue02;
      baseBorderColor = colorUtils.red02;
      return { baseLabelColor, baseInputColor, baseBorderColor };
    default:
      baseLabelColor = colorUtils.grey06;
      baseInputColor = colorUtils.darkBlue02;
      baseBorderColor = colorUtils.grey05;
      return { baseLabelColor, baseInputColor, baseBorderColor };
  }
};

export const getBorderStateStyles = (state: TValidationState, stateColor: string): JssStyle => {
  const baseBorderStyles = {
    borderRadius: '0',
  };

  switch (state) {
    case 'none':
      return {
        ...baseBorderStyles,
        border: `1px solid ${stateColor}`,
      };
    case 'error':
      return {
        ...baseBorderStyles,
        border: `2px solid ${stateColor}`,
      };
    default:
      return {
        ...baseBorderStyles,
        border: `1px solid ${stateColor}`,
      };
  }
};

export const iconPosition = (labelPosition: TInputLabelPosition, type: string): Styles => {
  if (type === 'date') {
    if (labelPosition === 'default') {
      return {
        top: '20px',
        right: '48px',
      };
    } else {
      return {
        top: '10px',
        right: '48px',
      };
    }
  } else {
    if (labelPosition === 'default') {
      return {
        top: '20px',
        right: '16px',
      };
    } else {
      return {
        top: '10px',
        right: '16px',
      };
    }
  }
};

export const getInputPaddingStyles = (
  type: string,
  labelPosition: TInputLabelPosition,
  state: TValidationState
): JssStyle => {
  if (type === 'search') {
    if (labelPosition === 'default') {
      return {
        padding: state === 'error' ? '25px 49px 9px 42px' : '26px 50px 10px 43px',
      };
    } else {
      return {
        padding: state === 'error' ? '6px 50px 6px 32px' : '7px 50px 7px 32px',
      };
    }
  }

  if (labelPosition === 'default') {
    return {
      padding: state === 'error' ? '25px 15px 9px 15px' : '26px 16px 10px 16px',
    };
  }

  return {
    padding: state === 'error' ? '6px 15px' : '7px 16px',
  };
};

const getDefaultLabelStyle = (isHidden: boolean): JssStyle =>
  isHidden
    ? {
        position: 'absolute',
        width: '1px',
        height: '1px',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(1px,1px,1px,1px)',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
      }
    : {
        width: 'auto',
        height: 'auto',
        margin: 0,
        overflow: 'visible',
        clip: 'auto',
        clipPath: 'none',
        whiteSpace: 'normal',
      };

const getScreenReaderInputLabelJssStyle = (
  isHidden: boolean,
  labelPosition: TInputLabelPosition,
  labelWidth: BreakpointCustomizable<string>
): JssStyle => {
  const gapStyle = isHidden ? '0' : '8px';

  if (labelPosition === 'above') {
    return {
      label: {
        display: 'grid',
        position: 'relative',
        gap: gapStyle,
        gridTemplateColumns: '1fr',

        '& > *': {
          minWidth: '0',
        },
      },
      '#label-container': {
        color: colorUtils.grey06,
      },
      '#label-text': {
        cursor: 'pointer',
        display: 'block',
        ...FULL_TEXT_STYLES['label-input'].focus,
        ...getScreenReaderJssStyle(isHidden),
      },
    };
  }

  if (labelPosition === 'left') {
    return {
      label: {
        display: 'grid',
        position: 'relative',
        gap: gapStyle,
        ...buildResponsiveStyles(labelWidth, (labelWidthResponsive: string) => ({
          gridTemplateColumns: isHidden ? '100%' : `${labelWidthResponsive} 1fr`,
        })),

        '& > *': {
          minWidth: '0',
        },
      },
      '#label-container': {
        display: 'grid',
        gridTemplateRows: isHidden ? 'auto' : '32px auto',
        alignItems: 'center',
        color: colorUtils.grey06,
      },
      '#label-text': {
        cursor: 'pointer',
        display: 'block',
        ...FULL_TEXT_STYLES['label-input'].focus,
        ...getScreenReaderJssStyle(isHidden),
      },
    };
  }

  if (labelPosition === 'default') {
    return {
      ...getDefaultLabelStyle(isHidden),
    };
  }
};

export const getLabelTextStyles = (
  labelPosition: TInputLabelPosition,
  hover: boolean
): JssStyle => {
  if (labelPosition === 'default') {
    if (hover) {
      return {
        ...FULL_TEXT_STYLES['label-textarea'].focus,
      };
    }

    return {
      ...FULL_TEXT_STYLES['label-textarea'].base,
    };
  }

  if (labelPosition === 'above' || labelPosition === 'left') {
    return {
      ...FULL_TEXT_STYLES['label-textarea'].focus,
    };
  }
};

export const getLabelStyles = (
  type: string,
  labelPosition: TInputLabelPosition,
  hideLabel: BreakpointCustomizable<boolean>,
  labelColor: string,
  labelWidth: BreakpointCustomizable<string>
): Styles => {
  if (labelPosition === 'default') {
    return {
      label: {
        display: 'grid',
        position: 'relative',
        gridTemplateColumns: '1fr',

        '& > *': {
          minWidth: '0',
        },

        '&.focus #label-text': {
          inset: type === 'search' ? '10px auto auto 43px' : '10px auto auto 16px',
          ...getLabelTextStyles(labelPosition, true),
        },
      },
      '#label-container': {
        color: labelColor,
        zIndex: 1,
      },
      '#label-text': mergeDeep({
        fontFamily: 'Bosch Sans',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out 0.1s',
        position: 'absolute',
        inset: type === 'search' ? '15px auto auto 43px' : '15px auto auto 16px',
        ...getLabelTextStyles(labelPosition, false),
        ...buildResponsiveStylesWithProps(
          hideLabel,
          getScreenReaderInputLabelJssStyle,
          labelPosition
        ),
      }),
    };
  }

  if (labelPosition === 'left' || labelPosition === 'above') {
    return mergeDeep(
      buildResponsiveStylesWithProps(
        hideLabel,
        getScreenReaderInputLabelJssStyle,
        labelPosition,
        labelWidth
      )
    );
  }
};

const getFileInputStyles = (): Styles => {
  return {
    label: {
      display: 'flex',
    },
  };
};

export const getSearchButtonStyles = (labelPosition: TInputLabelPosition): Styles => {
  if (labelPosition === 'default') {
    return {
      padding: '0 16px',
      height: '100%',
    };
  } else {
    return {
      padding: '0 16px',
      height: '32px',
    };
  }
};

export const getDateIconStyles = (labelPosition: TInputLabelPosition): Styles => {
  if (labelPosition === 'default') {
    return {
      top: '20px',
    };
  } else {
    return {
      top: '8px',
    };
  }
};

export const getSearchIconStyles = (labelPosition: TInputLabelPosition): Styles => {
  if (labelPosition === 'default') {
    return {
      top: '20px',
      left: '16px',
    };
  } else {
    return {
      top: '8px',
      left: '8px',
    };
  }
};

export const returnTypeStyles = (
  type: string,
  labelPosition: TInputLabelPosition,
  labelWidth: BreakpointCustomizable<string>,
  hideLabel: BreakpointCustomizable<boolean>,
  labelColor: string
): Styles => {
  switch (type) {
    case 'text':
      return {
        ...getLabelStyles(type, labelPosition, hideLabel, labelColor, labelWidth),
      };

    case 'search':
      return {
        ...getLabelStyles(type, labelPosition, hideLabel, labelColor, labelWidth),
      };

    case 'date':
      return {
        ...getLabelStyles(type, labelPosition, hideLabel, labelColor, labelWidth),
      };

    case 'email':
      return {
        ...getLabelStyles(type, labelPosition, hideLabel, labelColor, labelWidth),
      };

    case 'number':
      return {
        ...getLabelStyles(type, labelPosition, hideLabel, labelColor, labelWidth),
      };

    case 'password':
      return {
        ...getLabelStyles(type, labelPosition, hideLabel, labelColor, labelWidth),
      };

    case 'tel':
      return {
        ...getLabelStyles(type, labelPosition, hideLabel, labelColor, labelWidth),
      };

    case 'url':
      return {
        ...getLabelStyles(type, labelPosition, hideLabel, labelColor, labelWidth),
      };

    case 'file':
      return {
        ...getFileInputStyles(),
      };
  }
};
